import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#1C2541",
    colorBackground: "#ffffff",
    colorText: "#333333",
    fontFamily: "Arial, sans-serif",
    borderRadius: "4px",
  },
};

const CheckoutForm = ({ onSuccess, onError, clientDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setErrorMessage("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/#/reservationCheckout/confirmation`,
        payment_method_data: {
          billing_details: {
            name: clientDetails?.customer_name || undefined,
            email: clientDetails?.customer_email || undefined,
            phone: clientDetails?.customer_telephone
              ? `${clientDetails.countryCode || ""} ${clientDetails.customer_telephone}`.trim()
              : undefined,
          },
        },
      },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message);
      onError(error);
      setProcessing(false);
    } else if (paymentIntent?.status === "succeeded") {
      onSuccess(paymentIntent);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <PaymentElement options={{ layout: "tabs" }} />
      {errorMessage && (
        <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
          {errorMessage}
        </Typography>
      )}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || processing}
        startIcon={processing ? null : <LockIcon fontSize="small" />}
        sx={{
          mt: 2,
          py: 1.5,
          backgroundColor: "#1C2541",
          "&:hover": { backgroundColor: "#263677" },
          fontSize: 15,
          fontWeight: "bold",
          textTransform: "none",
        }}
      >
        {processing ? (
          <CircularProgress size={22} sx={{ color: "white" }} />
        ) : (
          "Pay Securely"
        )}
      </Button>
    </Box>
  );
};

const StripeCheckout = ({ amount, onSuccess, onError, clientDetails }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    fetch("https://app.autocrescente.com/WhyNotApi/api/payments/create-intent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setFetchError(true);
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false));
  }, [amount]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: "#1C2541" }} />
      </Box>
    );
  }

  if (fetchError || !clientSecret) {
    return (
      <Typography color="error" variant="body2" sx={{ mt: 1 }}>
        Failed to initialise payment. Please refresh and try again.
      </Typography>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <CheckoutForm onSuccess={onSuccess} onError={onError} clientDetails={clientDetails} />
    </Elements>
  );
};

export default StripeCheckout;
